import { request, requestWithMsg, uploadFile, MsgResult, PageResult } from './request';

// v4 (2026-07-22, 依据 doc/improve-arch.md): 参数按工艺框架多态拆表.
// 一行 = 一条参数记录 (无集/行两级); 所有参数接口路径带 archCode, 后端经 process_arch 注册表路由.

/** 材料键粒度: 决定参数行用哪组材料键字段. */
export type MaterialKeyKind = 'xcode' | 'dual_xcode' | 'category_code';

export interface ProcessArch {
    id: number;
    code: string;
    label: string;
    icon?: string;
    sort: number;
    isEnabled: boolean;
    /** 该框架参数表名 (param_laser/...); 空 = 未落参数表的占位框架 (如 embroidery) */
    paramTable: string;
    materialKeyKind: MaterialKeyKind;
}

/** 该框架是否已落参数表 (可创建/查询参数行). */
export function archUsable(a: ProcessArch): boolean {
    return !!a.paramTable;
}

// ParamSchema 字段模板 (参-04 统一模板管理).
// 模板键 (2026-11-01) = (archCode, deviceKey, groupCode, processType): 按设备建, 一个机型多台设备各一份;
// groupCode / processType 空串 = 通配. 新建参数行按键最特异匹配取字段集.
// deviceKey 为空的是存量未按设备拆分的模板 (按 family / deviceKeys 过渡期命中, 只能改字段/删, 不能新建).
export interface ParamSchema {
    id: number;
    archCode: string;
    /** 模板所属设备 (device.deviceKey); 空 = 存量未拆分模板 */
    deviceKey: string;
    /** 存量未拆分模板的机型 (device.family); 按设备建的模板恒为空 */
    family: string;
    /** 加工模式 (mm_group.code); 空=通配 */
    groupCode: string;
    /** 加工类型 (dict process_type); 空=通配 */
    processType: string;
    /** 存量模板的设备绑定清单 (2026-11-01 起只读, 拆分完下线) */
    deviceKeys?: string[];
    fields: string; // JSON string of FieldSpec[]
    /** 页签 → 分组 结构 JSON 串 (2026-10-23); null = 未启用分组. 解析见 utils/fieldGroups.parseLayout */
    layout?: string | null;
    createdAt?: string;
    updatedAt?: string;
}

/** 模板的设备段是否与已选设备 / 机型相容 (候选收窄用, 与后端 models.ParamSchema.Covers 同口径, 改一边要同步另一边):
 *  按设备建的模板只认本设备 (未选设备不收窄); 存量未拆分模板按绑定清单 + 机型 (任一为空 = 不限). */
export function schemaCovers(s: ParamSchema, deviceKey: string, family: string): boolean {
    if (s.deviceKey) return !deviceKey || s.deviceKey === deviceKey;
    if (deviceKey && s.deviceKeys?.length && !s.deviceKeys.includes(deviceKey)) return false;
    return !s.family || !family || s.family === family;
}

/** SchemaMatchKey 模板命中打分需要的键段. */
export type SchemaMatchKey = Pick<ParamSchema, 'deviceKey' | 'family' | 'groupCode' | 'processType'> & {
    deviceKeys?: string[];
};

/** bestSchema 候选里对某参数行上下文最特异的一张 (都不命中 = undefined).
 *  打分与后端 models.ParamSchema.MatchScore 一致, 改一边要同步另一边:
 *  本设备模板 16 > 存量清单 8 > 存量机型 4 > 模式组 2 > 加工类型 1; 模板段为空 = 通配, 非空须相等. */
export function bestSchema<T extends SchemaMatchKey>(
    tpls: T[], deviceKey: string, family: string, groupCode: string, processType: string,
): T | undefined {
    let best: T | undefined;
    let bestScore = -1;
    for (const t of tpls) {
        let score = 0;
        if (t.deviceKey) {
            if (t.deviceKey !== deviceKey) continue;
            score += 16;
        } else {
            if (t.deviceKeys?.length) {
                if (!t.deviceKeys.includes(deviceKey)) continue;
                score += 8;
            }
            if (t.family) {
                if (t.family !== family) continue;
                score += 4;
            }
        }
        if (t.groupCode) {
            if (t.groupCode !== groupCode) continue;
            score += 2;
        }
        if (t.processType) {
            if (t.processType !== processType) continue;
            score += 1;
        }
        if (score > bestScore) {
            best = t;
            bestScore = score;
        }
    }
    return best;
}

/** 模板关联参数清单 (最特异命中该模板的参数行); 只在删除校验/查看清单时按需查, 列表不带.
 *  items 最多 200 条, 超出 truncated=true. */
export interface ParamSchemaBindings {
    total: number;
    truncated: boolean;
    items: { name: string; code: string }[];
}

/** 参数行 (统一超集): 材料键三组按框架 materialKeyKind 三选一有效. */
export interface ParamRow {
    id: number;
    uuid: string;
    /** laser / knife: 具体材料 */
    xcode?: string;
    /** thermal: 热压膜 (主材料) */
    filmXcode?: string;
    /** thermal: 基材; 单材料加工类型 (烘烤) 为空 */
    substrateXcode?: string;
    /** inkjet: 二级类别编码 (categoryCode; 只在一级下唯一, 与 type1 成对定位; 2026-10-26 由 categoryCode 改名) */
    type2?: string;
    /** inkjet: 一级类别编码 (存量行回填为原父级) */
    type1?: string;
    deviceKey: string;
    /** 加工类型 (dict process_type, scope=archCode) */
    subProcess?: string;
    /** 字段定义 (JSON string of FieldSpec[]) */
    fields?: string;
    /** 建行时快照的模板分组布局 JSON 串 (2026-10-23); null = 未启用分组 */
    layout?: string | null;
    /** 参数值 {key:value} (JSON string) */
    params?: string;
    legacyMaterialId?: number;
    sourceTable?: string;
    sourceId?: number;
    resultTag: string;
    status: string; // draft / reviewing / rejected / published
    extra?: string;
    createdBy: number;
    updatedBy: number;
    remark?: string;
    createdAt?: string;
    updatedAt?: string;
    /** 列表接口附带 */
    archCode?: string;
    // 效果图/矩阵 2026-07-30 起内嵌参数行 (与源 material_process_para 同名同义)
    /** 效果图完整 URL, 可空 */
    effectImage?: string;
    /** 是否矩阵 */
    supportMatrix?: boolean;
    /** 矩阵数据 JSON string: {frontImg,row,col,xType,yType,轴范围,matrixPoint,...}; 非矩阵为空 */
    paraMatrix?: string;
    /** 非空 = 仅对该加工模式生效的覆盖行 (等于 params.processingMode); 空 = 同组加工模式共用 */
    overrideModeValue?: string;
}

export interface ParamRowDetail extends ParamRow {
    /** 按本行键最特异匹配出来的字段模板 (参-04): 详情表单的字段集以它为准 */
    schema?: ParamSchema;
    /** 命中模板的键 (空段=通配, 全空=框架默认模板), 供 UI 说明字段来自哪张模板 */
    schemaKey?: { archCode?: string; deviceKey?: string; family?: string; groupCode?: string; processType?: string };
}

export interface ListParamsParams {
    /** 模糊搜 材料键 / 设备 key */
    keyword?: string;
    deviceKey?: string;
    /** 机型 (e.g. S1, F2); 跟 deviceKey 二选一, 后端 sub-query 反查 device.family. */
    family?: string;
    /** 精确匹配主材料键列 (xcode / film_xcode / type2, 语义随框架) */
    materialKey?: string;
    /** 主材料键前缀模糊 (搜索框用) */
    materialKeyLike?: string;
    /** inkjet: 一级类别编码, 与 materialKey / materialKeyLike (二级类别) 同时用, 区分同名二级 */
    type1?: string;
    /** 仅 thermal (dual_xcode) 生效 */
    substrateXcode?: string;
    subProcess?: string;
    /** 加工模式值 (params.processingMode, mm_mode.value) 精确匹配 */
    processingMode?: string;
    status?: string;
    resultTag?: string;
    page?: number;
    pageSize?: number;
    /** true = 后端裁掉行级 fields / paraMatrix 大 JSON (响应留空串) 并跳过枚举富化.
     * 列表页用 (预览只用 params + 全局字段字典); 导出要行级 fields, 不传. */
    lite?: boolean;
}

/** 新建/更新参数行的载荷 (材料键按框架粒度三选一). */
export interface ParamRowPayload {
    xcode?: string;
    filmXcode?: string;
    substrateXcode?: string;
    /** inkjet: 二级类别编码 */
    type2?: string;
    /** inkjet: 一级类别编码 (与 type2 成对必填) */
    type1?: string;
    deviceKey?: string;
    subProcess?: string;
    fields?: string;
    /** 分组布局 JSON 串 (2026-10-23); null = 未启用分组; 不传 = 不改 */
    layout?: string | null;
    params?: string;
    resultTag?: string;
    extra?: string;
    /** 效果图 URL (PATCH 传空串 = 清除) */
    effectImage?: string;
    supportMatrix?: boolean;
    /** 矩阵数据 JSON string (PATCH 传空串 = 清除) */
    paraMatrix?: string;
    /** 仅对该加工模式生效 (须等于 params.processingMode); 空串 = 同组加工模式共用 */
    overrideModeValue?: string;
    remark?: string;
}

const BASE = '/v1';

export function listProcessArchs(): Promise<ProcessArch[]> {
    return request<ProcessArch[]>(`${BASE}/process-archs`);
}

export function getSchema(archCode: string): Promise<ParamSchema> {
    return request<ParamSchema>(`${BASE}/param-schemas/by-arch`, { method: 'POST', dedupe: true, body: { archCode } });
}

/** 模板键: deviceKey + groupCode + processType (空段 = 通配); family 只用来定位存量未拆分模板. */
export interface SchemaKey {
    deviceKey?: string;
    family?: string;
    groupCode?: string;
    processType?: string;
}

/** 模板列表 (统一模板管理页). 空 arch = 全部框架. */
export function listSchemas(arch = ''): Promise<ParamSchema[]> {
    return request<ParamSchema[]>(`${BASE}/param-schemas`, { query: { arch } });
}

/** 模板关联了哪些参数行 (最特异命中该模板的行; 删除保护看的也是这份数据). */
export function listSchemaBindings(arch: string, key: SchemaKey): Promise<ParamSchemaBindings> {
    return request<ParamSchemaBindings>(`${BASE}/param-schemas-bindings`, {
        query: {
            arch,
            deviceKey: key.deviceKey || '',
            family: key.family || '',
            group: key.groupCode || '',
            processType: key.processType || '',
        },
    });
}

/** 按键最特异匹配模板 (参数录入选定 设备/加工模式/加工类型 后拉字段集).
 *  key.family 只供存量未拆分模板过渡期命中 (不传时后端按 deviceKey 查机型). */
export function resolveSchema(
    arch: string,
    key: SchemaKey,
    deviceKey = '',
): Promise<ParamSchema> {
    return request<ParamSchema>(`${BASE}/param-schemas-resolve`, {
        query: {
            arch,
            family: key.family || '',
            group: key.groupCode || '',
            processType: key.processType || '',
            deviceKey,
        },
    });
}

// 按模板键保存字段模板 (覆盖式). 统一走审批工单 (message 含"已提交审批").
// 修改模板只影响 "新建参数行" 的字段集, 不影响已有参数行 (字段定义随行本身).
export function upsertSchema(
    archCode: string,
    fields: string,
    key: SchemaKey = {},
    /** 分组布局 JSON 串 (2026-10-23): undefined = 不传 (沿用模板现有布局); null = 取消分组 */
    layout?: string | null,
): Promise<MsgResult<{ id?: number; archCode?: string; taskCode?: string }>> {
    return requestWithMsg(`${BASE}/param-schemas/upsert`, {
        method: 'POST',
        body: {
            archCode,
            fields,
            ...(layout === undefined ? {} : { layout: layout ?? '' }),
            deviceKey: key.deviceKey || '',
            family: key.family || '',
            groupCode: key.groupCode || '',
            processType: key.processType || '',
        },
    });
}

/** 按模板键删除模板 (有参数行命中时后端拒删; 框架默认模板同样可删). */
export function deleteSchema(
    archCode: string,
    key: SchemaKey,
): Promise<MsgResult<null>> {
    return requestWithMsg(`${BASE}/param-schemas/delete`, {
        method: 'POST',
        body: {
            archCode,
            deviceKey: key.deviceKey || '',
            family: key.family || '',
            groupCode: key.groupCode || '',
            processType: key.processType || '',
        },
    });
}

export function listParams(archCode: string, params: ListParamsParams): Promise<PageResult<ParamRow>> {
    // 键走 body (2026-08-24): archCode 等业务键不再放路径; 筛选条件一并进 body.
    return request<PageResult<ParamRow>>(`${BASE}/params/list`, {
        method: 'POST', dedupe: true,
        body: { archCode, ...params },
    });
}

/** 各框架命中行数 (跨框架搜索定位): 同一筛选在全部启用框架下各数一遍, 键 = archCode.
 * 口径与 listParams 完全一致 (后端复用同一条件构造), badge 数 = 切到该框架看到的 totalCount. */
export function getParamArchHits(params: ListParamsParams): Promise<Record<string, number>> {
    return request<Record<string, number>>(`${BASE}/params/arch-hits`, {
        method: 'POST', dedupe: true,
        body: params,
    });
}

/** 各状态行数 (KPI 卡): 后端一条 GROUP BY + 缓存; 键 = status (published/draft/reviewing/...), 缺键 = 0 行. */
export function getParamStats(archCode: string): Promise<Record<string, number>> {
    return request<Record<string, number>>(`${BASE}/params-stats`, { query: { arch: archCode } });
}

export function getParam(archCode: string, id: number): Promise<ParamRowDetail> {
    return request<ParamRowDetail>(`${BASE}/params/detail`, { method: 'POST', dedupe: true, body: { archCode, id } });
}

export function createParam(
    archCode: string,
    payload: ParamRowPayload & { deviceKey: string },
): Promise<{ id: number; status: string }> {
    return request(`${BASE}/params/create`, { method: 'POST', body: { archCode, ...payload } });
}

export function updateParam(archCode: string, id: number, payload: ParamRowPayload): Promise<null> {
    return request(`${BASE}/params/update`, { method: 'POST', body: { archCode, id, ...payload } });
}

export function deleteParam(archCode: string, id: number): Promise<null> {
    return request(`${BASE}/params/delete`, { method: 'POST', body: { archCode, id } });
}

// 结果标 (result_tag): 这组参数的实测结论 ----------------------------
//
// 与参数值同属一行数据, 改它就跟改参数一样走 PATCH → 强制回落 draft →
// 提交评审 → 管理员审批 → published, 不另开审批通道。

/**
 * 结果标枚举 (与后端 models.IsValidResultTag 同源).
 *
 * 2026-08-27 废止历史值 pending: 它原本是"录入人还没给结论"的默认值, 与审批状态 (status 列) 无关,
 * 但被显示成「待审批」造成误读; 参数只有审批通过才进列表/下发下游, 没结论的行没有意义。
 * 现在结果标**必填**, 新建/导入不给或给非法值后端直接拒绝。
 */
export const RESULT_TAGS: { v: string; label: string }[] = [
    { v: 'recommended', label: '✅推荐' },
    { v: 'unsupported', label: '❌不支持' },
];

export const RESULT_TAG_LABELS: Record<string, string> = Object.fromEntries(
    RESULT_TAGS.map((t) => [t.v, t.label]),
);

// 评审流转 ----------------------------------------------------------

export interface ValidateResult {
    pass: boolean;
    issues: string[] | null;
}

/** Schema 静态校验 (行 params vs 行 fields). */
export function validateParam(archCode: string, id: number): Promise<ValidateResult> {
    return request(`${BASE}/params/validate`, {
        method: 'POST',
        body: { archCode, id },
    });
}

/** 提交评审: draft → reviewing, 建 approval_task (target_type=archCode, target_code=行id). */
export function submitParam(archCode: string, id: number, summary?: string): Promise<{ taskCode: string; taskId: number }> {
    return request(`${BASE}/params/submit`, {
        method: 'POST',
        body: { archCode, id, summary: summary || '' },
    });
}

/** 撤回评审: 仅 submittedBy / admin. */
export function withdrawParam(archCode: string, id: number): Promise<null> {
    return request(`${BASE}/params/withdraw`, {
        method: 'POST',
        body: { archCode, id },
    });
}

// 审批中心 ----------------------------------------------------------

export interface ApprovalTask {
    id: number;
    uuid: string;
    taskCode: string;
    /**
     * 多态:
     * - 参数行审批: 存 arch_code (laser/knife/thermal/inkjet)
     * - 通用变更工单: 存资源类型 (material/material_sku/phys_value/dict/category/device/cap_type)
     */
    targetType: string;
    /** 参数行 id (字符串化) 或 资源业务键 (xcode/skuCode/code/...) */
    targetCode: string;
    /** 通用变更工单的变更内容 (JSON: {action, data}); 参数行审批无此字段 */
    payload?: string;
    title: string;
    changeSummary?: string;
    submittedBy: number;
    /** 提交人可读名 (建单时快照); 存量旧单为空, 展示时回退 submittedBy. */
    submittedByName?: string;
    approvedBy?: number;
    status: 'pending' | 'approved' | 'rejected' | 'cancelled';
    priority: number;
    decidedAt?: string;
    decisionNote?: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface ApprovalRecord {
    id: number;
    taskId: number;
    action: 'submit' | 'approve' | 'reject' | 'withdraw' | 'comment';
    fromStatus?: string;
    toStatus?: string;
    comment?: string;
    createdBy: number;
    createdAt?: string;
}

export interface ApprovalDetail extends ApprovalTask {
    records: ApprovalRecord[];
}

export interface ListApprovalParams {
    keyword?: string;
    status?: string;
    page?: number;
    pageSize?: number;
    /** 'mine' = 仅看自己提交的; admin 不传则看全部 */
    scope?: 'mine';
    /**
     * 精确过滤 target_type. 支持逗号分隔多值 (如 "category,dict");
     * 特殊值 "param" 由后端展开为 process_arch 注册表全部 code (参数行审批).
     */
    targetType?: string;
    /** 提交人筛选: 名字/邮箱子串匹配 (纯数字再匹配 uid) */
    submitter?: string;
}

export function listApprovals(params: ListApprovalParams): Promise<PageResult<ApprovalTask>> {
    return request<PageResult<ApprovalTask>>(`${BASE}/approvals`, { query: params });
}

/** pending 工单按 target_type 分组计数 (审批中心模块 tab 徽标). 可见域与列表一致. */
export interface ApprovalStat {
    targetType: string;
    count: number;
}

/** scope='mine' 时只统计自己提交的 (「我的工单」页, 与列表口径一致); 普通用户后端恒按本人收紧. */
export function listApprovalStats(scope?: 'mine'): Promise<ApprovalStat[]> {
    return request<ApprovalStat[]>(`${BASE}/approvals/stats`, { query: { scope } });
}

export function getApproval(taskCode: string): Promise<ApprovalDetail> {
    return request<ApprovalDetail>(`${BASE}/approvals/${encodeURIComponent(taskCode)}`);
}

export function approveTask(taskCode: string, note?: string): Promise<null> {
    return request(`${BASE}/approvals/${encodeURIComponent(taskCode)}/approve`, {
        method: 'POST',
        body: { note: note || '' },
    });
}

export function rejectTask(taskCode: string, note?: string): Promise<null> {
    return request(`${BASE}/approvals/${encodeURIComponent(taskCode)}/reject`, {
        method: 'POST',
        body: { note: note || '' },
    });
}

export function commentTask(taskCode: string, comment: string): Promise<null> {
    return request(`${BASE}/approvals/${encodeURIComponent(taskCode)}/comment`, {
        method: 'POST',
        body: { comment },
    });
}

// ---- 批量导入 (xlsx) ------------------------------------------------------

/** 参数导入模板下载路径 (按框架/参数模板动态生成列; 下载必须走 downloadFile 带鉴权). */
/** 导入模板预设的关键字段: 选了哪项, xlsx 就不出哪列, 上传时由后端补到每行 (都可空) */
export interface ParamImportPreset {
    deviceKey?: string;
    processingMode?: string;
    subProcess?: string;
}

// 导入模板下载地址. 不传 tplId 时后端按预填的 型号/加工模式/加工类型 最特异解析模板
// (与录入命中同一逻辑); 都没选 = 框架默认模板的字段列.
export function paramImportTemplatePath(
    archCode: string,
    preset: ParamImportPreset = {},
    tplId = 0,
): string {
    const q = new URLSearchParams({ arch: archCode });
    if (tplId > 0) q.set('tplId', String(tplId));
    for (const [k, v] of Object.entries(preset)) if (v) q.set(k, v);
    return `${BASE}/params-import-template?${q.toString()}`;
}

export interface ParamImportInvalidRow {
    row: number;
    reason: string;
}

export interface ImportParamsResult {
    total: number;
    valid: number;
    invalid: ParamImportInvalidRow[] | null;
}

/**
 * 参数批量导入 (multipart xlsx + archCode).
 * 动态字段列必须命中 schema_field 字典, 未知列整文件报错;
 * 有效行落库为 draft, 走既有 submit/approve 评审发布.
 */
export function importParams(archCode: string, file: File): Promise<ImportParamsResult> {
    return uploadFile<ImportParamsResult>(`${BASE}/params-import-xlsx`, file, 'file', {
        archCode,
    });
}
